import * as React from 'react';

import {
    BORDER_OPACITY,
    BRIGHTNESS_INCREMENT,
    DEFAULT_OPACITY,
    MINIMUM_TEXT_CONTRAST_REST,
    MINIMUM_TEXT_CONTRAST_SELECTED,
    OPACITY_BUSY,
    OPACITY_FREE,
    OPACITY_INCREMENT,
    OPACITY_TENTATIVE,
    REST_SATURATION_INCREASE,
} from '../utils/constants';
import { CharmControl, CharmControlColorPattern, CharmControlColorProps } from 'owa-charmcontrol';
import { CharmInfo, getCharmForId } from 'owa-calendar-charms-store';
import ColorScheme, { colorSchemeToCss } from 'owa-color-utils/lib/ColorScheme';
import {
    FreeBusyConstants,
    FreeBusyType,
    getFreeBusyItemBarClass,
    getFreeBusyItemBarStyling,
    getFreeBusyItemWrapperClass,
} from 'owa-freebusy-style';
import { HighContrastSelector, IStyle } from '@fluentui/style-utilities';
import { JoinButtonLabelFormat, JoinOnlineMeetingButton } from 'owa-online-meeting/lib/bootIndex';
import {
    addBrightnessToColor,
    addOpacityToColor,
    addSaturationToColor,
    computeResultingOverlapColor,
} from 'owa-color-utils/lib/colorUtils';

import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import CalendarItemTypeConstants from '../store/schema/CalendarItemType';
import { Icon } from '@fluentui/react/lib/Icon';
import type { ResponseTypeType } from '../store/schema/ResponseTypeType';
import { SurfaceConstants } from 'owa-calendar-surface-core';
import type { SurfaceItemState } from '../interfaces/SurfaceItemState';
import { getCalendarEventIcons } from 'owa-calendar-event-icons';
import { getOptimalTextColor } from 'owa-color-utils/lib/OptimalTextColorCache';
import { getPaletteAsRawColors } from 'owa-theme';
import { isItemMultiDay } from 'owa-calendar-events-visual-date-range';

let classNames = require('classnames');

/**
 * Renders the free busy bar for the given item, using duration and free busy type
 * @param item The item to render the free busy bar for
 */
export function renderFreeBusyBar(
    freeBusyType: FreeBusyType,
    duration: number,
    colorScheme: ColorScheme
) {
    if (freeBusyType == null) {
        // don't render if there's no free busy type
        return null;
    }
    let freeBusySpecificClass = getFreeBusyItemBarClass(freeBusyType);

    let freeBusyStyling = getFreeBusyItemBarStyling(freeBusyType, colorScheme, duration);

    return <div className={freeBusySpecificClass} style={freeBusyStyling} />;
}

/**
 * Renders the Join button for Online meetings
 * @param item The item that can be joinable Online
 * @param colorScheme The color scheme to use to render the Join button
 * @param eventWidth Event's box width
 * @param joinButtonWidth Join button width
 * @param styles css styles that can be set to the Join Button
 * @param isShorter Whether this item is using shorter item styling
 */
export function renderJoinButton(
    item: CalendarEvent,
    colorScheme: ColorScheme,
    eventWidth: number,
    joinButtonWidth: number,
    styles?: string,
    isShorter?: boolean
): JSX.Element {
    const surfaceStyles = require('./CalendarSurfaceItem.scss');
    let buttonStyle = null;

    if (!styles) {
        buttonStyle = surfaceStyles.joinButton;
    } else {
        buttonStyle = styles;
    }

    let fullFormValue: JoinButtonLabelFormat = 'SurfaceCompact';

    const joinButtonInEventRatio = joinButtonWidth / eventWidth;
    if (
        // Button doesn't exist yet but fits with label
        (joinButtonWidth == 0 &&
            eventWidth > 0 &&
            eventWidth >= SurfaceConstants.minWidthToShowJoinButtonLabel) ||
        // Button exists and fits with label
        (joinButtonWidth > 0 &&
            joinButtonInEventRatio >= 0 &&
            joinButtonInEventRatio <= SurfaceConstants.ratioToShowJoinButtonLabel &&
            eventWidth >= SurfaceConstants.minWidthToShowJoinButtonLabel &&
            joinButtonWidth > SurfaceConstants.defaultJoinButtonWidth)
    ) {
        fullFormValue = 'SurfaceFull';
        buttonStyle =
            joinButtonWidth > 0 ? styles : classNames(styles, surfaceStyles.joinButtonHidden);
    } else if (
        // Button doesn't exist yet but fits with video icon
        (joinButtonWidth == 0 &&
            eventWidth > 0 &&
            eventWidth < SurfaceConstants.minWidthToShowJoinButtonLabel) ||
        // Button exists and fits with video icon
        (joinButtonWidth >= 0 &&
            joinButtonInEventRatio >= 0 &&
            eventWidth >= SurfaceConstants.minWidthToShowJoinButtonIcon)
    ) {
        fullFormValue = 'SurfaceCompact';
        buttonStyle = classNames(styles, surfaceStyles.joinButtonCompactWidth);
    } else {
        return null;
    }

    return (
        <JoinOnlineMeetingButton
            event={item}
            colorScheme={colorScheme}
            actionSource={'Surface'}
            className={classNames(buttonStyle, { [surfaceStyles.shortButton]: isShorter })}
            fullForm={fullFormValue}
        />
    );
}

/**
 * Renders the charm wrapper for the given item
 * @param item The item being rendered
 * @param colorScheme The color scheme to be used
 * @param charmId The id of the charm
 * @param styles Optional separate style sheet to use
 * @param isShortest Whether this item is using shorter item styling
 */
export function renderCharmWrapper(
    item: CalendarEvent,
    colorScheme: ColorScheme,
    charmId: number,
    styles?: any,
    isShortest?: boolean
) {
    if (!styles) {
        styles = require('./CalendarSurfaceItem.scss');
    }

    let charm = getCharmForId(charmId) as CharmInfo;

    let colorProps: CharmControlColorProps = {
        colorPattern: CharmControlColorPattern.TextColor,
        calPrimary: colorScheme.calPrimary,
        calDark: colorScheme.calDark,
        calDarkAlt: colorScheme.calDarkAlt,
        textColor: colorScheme.accessibleText.calPrimaryBackground,
        inheritTextColor: true,
    };

    return (
        charm.IconId > 0 &&
        item.CalendarItemType !== CalendarItemTypeConstants.Exception && (
            <div className={isShortest ? styles.shortestItemCharmWrapper : styles.charmWrapper}>
                <div
                    className={isShortest ? styles.shortestItemCalendarIcon : styles.calendarIcon}
                    title={charm.Name}>
                    <CharmControl
                        iconId={charm.IconId}
                        className={styles.surfaceRowCharm}
                        colorProps={colorProps}
                    />
                </div>
            </div>
        )
    );
}

/**
 * Renders the charm wrapper for the given all day item
 * @param item The item being rendered
 * @param colorScheme The color scheme to be used
 * @param charmId The id of the charm
 */
export function renderCharmWrapperAllDayItem(
    item: CalendarEvent,
    colorScheme: ColorScheme,
    charmId: number
) {
    let allDayStyles = require('./CalendarAllDaySurfaceItem.scss');
    return renderCharmWrapper(item, colorScheme, charmId, allDayStyles);
}

/**
 * Renders the icon wrapper for the given item
 * @param item The item being rendered
 * @param isShorter Whether this item is using shorter item styling
 * @param itemState Indicates whether event is cutted to fit current surface date range
 * @param styles Optional separate style sheet to use
 * @param isShortest Whether this item is using shortest item styling
 */
export function renderIconWrapper(
    item: CalendarEvent,
    isShorter: boolean,
    itemState?: SurfaceItemState,
    styles?: any,
    isShortest?: boolean
): JSX.Element {
    if (!hasIcon(item, itemState)) {
        return null;
    }

    if (!styles) {
        styles = require('./CalendarSurfaceItem.scss');
    }

    const iconWrapperClass = isShortest
        ? styles.shortestIconWrapper
        : isShorter
        ? styles.shorterItemIconWrapper
        : isItemMultiDay(item)
        ? styles.multiDayItemIconWrapper
        : styles.iconWrapper;
    const iconClass = isShortest ? styles.shortestItemIcon : styles.icon;
    const children = getCalendarEventIcons(
        item.HasAttachments,
        item.CalendarItemType === CalendarItemTypeConstants.Occurrence,
        item.Sensitivity === 'Private',
        item.CalendarItemType === CalendarItemTypeConstants.Exception
    ).map((props, index) => <Icon {...props} className={iconClass} key={index} />);

    return children.length == 0 ? null : <div className={iconWrapperClass}>{children}</div>;
}

/**
 * Renders the icon wrapper for the given all day item
 * @param item The item being rendered
 */
export function renderIconWrapperAllDayItem(item: CalendarEvent, itemState: SurfaceItemState) {
    let allDayStyles = require('./CalendarAllDaySurfaceItem.scss');
    return renderIconWrapper(item, false, itemState, allDayStyles);
}

/**
 * Gets the class for the entire item based on the free busy status
 */
export function getItemWrapperClass(item: CalendarEvent) {
    let itemWrapperClassNames = classNames(
        getItemSubjectClass(item),
        getFreeBusyItemWrapperClass(item.FreeBusyType)
    );
    return itemWrapperClassNames;
}

/**
 * Gets the class for the entire item based on the isCancelled status
 */
function getItemSubjectClass(item: CalendarEvent) {
    let styles = require('./CalendarSurfaceItem.scss');
    return item.IsCancelled || item.ResponseType == 'Decline' ? styles.itemIsCancelled : null;
}

/**
 * Gets the styles for just the content of the item, not including the free busy bar
 * @param freeBusyType the item free busy type
 * @param responseType the item response type
 * @param colorScheme the item color scheme
 */
export function getItemContentStyles(
    freeBusyType: FreeBusyType,
    responseType: ResponseTypeType,
    colorScheme: ColorScheme,
    isCancelled: boolean,
    useBoldTheme: boolean
): IStyle {
    let colorCssNormal = colorSchemeToCss(colorScheme);
    let backgroundColor = colorCssNormal.backgroundColor;
    let textColor = colorCssNormal.color;
    let minimumTextContrastRest = MINIMUM_TEXT_CONTRAST_REST;
    let minimumTextContrastSelected = MINIMUM_TEXT_CONTRAST_SELECTED;

    let opacity = calculateWeekViewOpacity(freeBusyType, useBoldTheme);
    let borderWidth = calculateBorderWidth(freeBusyType);
    let rawPalette = getPaletteAsRawColors();

    /**
     * Calculate the background colors for the different interactive states, including their opacity
     *
     * in light theme, use background + opacity increment
     * in bright theme, use background and change brightness value (of HSB)
     */
    const restBackgroundColor = useBoldTheme
        ? backgroundColor
        : addSaturationToColor(backgroundColor, REST_SATURATION_INCREASE);
    let transparentBackground = addOpacityToColor(restBackgroundColor, opacity);
    let borderBackground = transparentBackground;
    let hoverBackground = useBoldTheme
        ? addBrightnessToColor(backgroundColor, -BRIGHTNESS_INCREMENT)
        : addOpacityToColor(restBackgroundColor, opacity + OPACITY_INCREMENT);
    let pressedBackground = useBoldTheme
        ? addBrightnessToColor(backgroundColor, -2 * BRIGHTNESS_INCREMENT)
        : addOpacityToColor(backgroundColor, 1); // intentionally NOT using restBackgroundColor, don't want the saturation increased

    /**
     * Since the background colors have opacity, compute the RESULTING color from overlaying that opacity with the surface ms-color-white background
     * so that we know what color we're working with when computing an accessible text color
     */
    let totalBackgroundColor = computeResultingOverlapColor(
        transparentBackground,
        rawPalette.white
    );
    let totalHoverBackgroundColor = computeResultingOverlapColor(hoverBackground, rawPalette.white);
    let totalPressedBackgroundColor = computeResultingOverlapColor(
        pressedBackground,
        rawPalette.white
    );

    /**
     * Using the calculated true background colors, calculate accessible text colors
     */
    textColor = getOptimalTextColor(
        totalBackgroundColor,
        colorScheme,
        rawPalette.white,
        minimumTextContrastRest
    );
    let hoveredTextColor = getOptimalTextColor(
        totalHoverBackgroundColor,
        colorScheme,
        textColor,
        minimumTextContrastRest
    );
    let pressedTextColor = getOptimalTextColor(
        totalPressedBackgroundColor,
        colorScheme,
        textColor,
        minimumTextContrastSelected
    );

    /**
     * if item not responded to, use tentative styling with dashed border
     */
    let borderStyle = useBoldTheme ? 'none' : 'solid';
    let borderInset = 0;
    let borderOpacity = opacity == 0 ? OPACITY_BUSY : opacity;
    if (responseType == 'NoResponseReceived' && !isCancelled) {
        borderStyle = 'dashed';
        borderBackground = backgroundColor;
        borderWidth = 1;
        borderOpacity = useBoldTheme ? 1 : BORDER_OPACITY;
        borderInset = useBoldTheme ? 1 : 0;
    }

    const borderColor = useBoldTheme
        ? rawPalette.white
        : addOpacityToColor(borderBackground, borderOpacity);

    const highContrastSelectorStyle: IStyle = {
        MsHighContrastAdjust: 'none',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'windowText',
        backgroundColor: 'highlight !important',
        color: 'window',
    };

    const restStyle: IStyle = {
        backgroundColor: transparentBackground,
        color: textColor,
        fill: textColor,
    };

    const hoverStyle: IStyle = {
        backgroundColor: hoverBackground,
        borderColor: borderColor,
        color: useBoldTheme ? textColor : hoveredTextColor,
        fill: useBoldTheme ? textColor : hoveredTextColor,
        selectors: {
            '&::after': {
                borderColor: borderColor,
            },
            [HighContrastSelector]: {
                ...highContrastSelectorStyle,
            },
        },
    };

    const activeStyle: IStyle = {
        backgroundColor: pressedBackground,
        borderColor: useBoldTheme ? textColor : addOpacityToColor(pressedBackground, borderOpacity),
        color: useBoldTheme ? textColor : pressedTextColor,
        fill: useBoldTheme ? textColor : pressedTextColor,
        selectors: {
            '&::after': {
                borderColor: borderColor,
            },
            [HighContrastSelector]: {
                ...highContrastSelectorStyle,
            },
        },
    };

    const selectedStyle: IStyle = useBoldTheme ? restStyle : activeStyle;

    return {
        ...restStyle,
        selectors: {
            '&::after': {
                position: 'absolute',
                content: '""',
                left: borderInset,
                right: borderInset,
                top: borderInset,
                bottom: borderInset,
                borderColor: borderColor,
                borderWidth: borderWidth,
                borderStyle: borderStyle,
                borderRadius: 2,
                borderLeft: useBoldTheme ? 'none' : '',
            },
            ['&:hover, .itemHovered &, .ms-Fabric--isFocusVisible .itemFocused &']: {
                ...hoverStyle,
            },
            '&:active, .itemFocused &:active': {
                ...activeStyle,
            },
            '.itemSelected &, .itemSelected &:hover, .itemSelected &:active, .itemFocused.itemSelected &': {
                ...selectedStyle,
            },
            ['.ms-Fabric--isFocusVisible .itemFocused &::after']: {
                border: 'none',
            },
        },
    };
}

/**
 * Gets the styles for just the content of the item, not including the free busy bar
 * @param freeBusyType the item free busy type
 * @param responseType the item response type
 * @param colorScheme the item color scheme
 */
export function getMonthItemContentStyles(
    freeBusyType: FreeBusyType,
    responseType: ResponseTypeType,
    colorScheme: ColorScheme,
    useBoldTheme: boolean
): IStyle {
    let colorCssNormal = colorSchemeToCss(colorScheme);
    let backgroundColor = colorCssNormal.backgroundColor;
    let rawPalette = getPaletteAsRawColors();
    let textColor = colorCssNormal.color;
    let minimumTextContrastRest = MINIMUM_TEXT_CONTRAST_REST;
    let minimumTextContrastSelected = MINIMUM_TEXT_CONTRAST_SELECTED;

    const opacity = calculateMonthViewOpacity(freeBusyType, useBoldTheme);

    const restBackgroundColor = useBoldTheme
        ? backgroundColor
        : addSaturationToColor(backgroundColor, REST_SATURATION_INCREASE);
    let transparentBackground = addOpacityToColor(restBackgroundColor, opacity);
    let hoverBackground = useBoldTheme
        ? addBrightnessToColor(backgroundColor, -BRIGHTNESS_INCREMENT)
        : addOpacityToColor(restBackgroundColor, opacity + OPACITY_INCREMENT);
    let pressedBackground = useBoldTheme
        ? addBrightnessToColor(backgroundColor, -2 * BRIGHTNESS_INCREMENT)
        : addOpacityToColor(backgroundColor, 1); // intentionally NOT using restBackgroundColor, don't want the saturation increased

    let totalBackgroundColor = computeResultingOverlapColor(
        transparentBackground,
        rawPalette.white
    );
    let totalHoverBackgroundColor = computeResultingOverlapColor(hoverBackground, rawPalette.white);
    let totalPressedBackgroundColor = computeResultingOverlapColor(
        pressedBackground,
        rawPalette.white
    );

    textColor = getOptimalTextColor(
        totalBackgroundColor,
        colorScheme,
        rawPalette.white,
        minimumTextContrastRest
    );
    let hoveredTextColor = getOptimalTextColor(
        totalHoverBackgroundColor,
        colorScheme,
        textColor,
        minimumTextContrastRest
    );
    let pressedTextColor = getOptimalTextColor(
        totalPressedBackgroundColor,
        colorScheme,
        textColor,
        minimumTextContrastSelected
    );

    const highContrastSelectorStyle: IStyle = {
        MsHighContrastAdjust: 'none',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'windowText',
        backgroundColor: 'highlight !important',
        color: 'window',
    };

    const restStyle: IStyle = {
        backgroundColor: transparentBackground,
        borderColor: 'transparent',
        color: textColor,
        fill: textColor,
        borderStyle: 'solid',
    };

    const hoverStyle: IStyle = {
        backgroundColor: hoverBackground,
        color: hoveredTextColor,
        fill: hoveredTextColor,
        selectors: {
            [HighContrastSelector]: {
                ...highContrastSelectorStyle,
            },
        },
    };

    const activeStyle: IStyle = {
        backgroundColor: pressedBackground,
        borderColor: pressedBackground,
        color: useBoldTheme ? textColor : pressedTextColor,
        fill: useBoldTheme ? textColor : pressedTextColor,
        selectors: {
            [HighContrastSelector]: {
                ...highContrastSelectorStyle,
            },
        },
    };

    const selectedStyle = useBoldTheme ? restStyle : activeStyle;

    const focusStyle: IStyle = {
        backgroundColor: hoverBackground,
        borderColor: pressedBackground,
        color: useBoldTheme ? textColor : hoveredTextColor,
        fill: useBoldTheme ? textColor : hoveredTextColor,
        selectors: {
            [HighContrastSelector]: {
                ...highContrastSelectorStyle,
            },
        },
    };

    return {
        ...restStyle,
        selectors: {
            '&:hover, .itemHovered &': {
                ...hoverStyle,
            },
            ['.ms-Fabric--isFocusVisible &:focus, .ms-Fabric--isFocusVisible .itemFocused &']: {
                ...focusStyle,
            },
            '&:active, .itemActive &, .itemActive &:hover': {
                ...activeStyle,
            },
            '.itemSelected &, .itemSelected &:hover, .itemSelected &:active, .ms-Fabric--isFocusVisible .itemFocused.itemSelected &': {
                ...selectedStyle,
            },
        },
    };
}

/**
 * Returns true if the item being rendered has any icons to display
 * @param item the item being rendered
 */
export function hasIcon(item: CalendarEvent, itemState: SurfaceItemState): boolean {
    return (
        item.CalendarItemType == CalendarItemTypeConstants.Occurrence ||
        item.CalendarItemType == CalendarItemTypeConstants.Exception ||
        item.HasAttachments ||
        item.Sensitivity == 'Private' ||
        itemState?.continuesRight
    );
}

function calculateWeekViewOpacity(freeBusyType: FreeBusyType, useBoldTheme: boolean): number {
    if (useBoldTheme) {
        return DEFAULT_OPACITY;
    }

    let opacity = OPACITY_BUSY;
    switch (freeBusyType) {
        case FreeBusyConstants.Tentative:
            opacity = OPACITY_TENTATIVE;
            break;
        case FreeBusyConstants.Free:
        case FreeBusyConstants.WorkingElsewhere:
            opacity = OPACITY_FREE;
    }

    return opacity;
}

function calculateMonthViewOpacity(freeBusyType: FreeBusyType, useBoldTheme: boolean): number {
    if (useBoldTheme) {
        return DEFAULT_OPACITY;
    }

    let opacity = OPACITY_FREE;
    switch (freeBusyType) {
        case FreeBusyConstants.Busy:
        case FreeBusyConstants.OOF:
            opacity = OPACITY_BUSY;
            break;
        default:
            opacity = OPACITY_FREE;
    }

    return opacity;
}

function calculateBorderWidth(freeBusyType: FreeBusyType): number {
    switch (freeBusyType) {
        case FreeBusyConstants.Free:
        case FreeBusyConstants.WorkingElsewhere:
            return 1;
    }

    return 0;
}
